import { formatNum } from '@app/utils/math';
import { useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { QUERY_KEY_MANAGER_PERFORMANCE } from '@entities/managers-perfomance/query-keys';
import { toast } from 'react-toastify';
import {
  useUpdateManagerMonthlyPlan,
  useUpdateManagerQuarterlyPlan,
  useUpdateManagerYearlyPlan,
} from '@entities/managers-perfomance';

type ProgressCardType = 'monthly' | 'quarterly' | 'yearly';

type PlanResult = {
  plan?: number | null;
};

type MonthlyUpdateReturn = ReturnType<typeof useUpdateManagerMonthlyPlan>;
type QuarterlyUpdateReturn = ReturnType<typeof useUpdateManagerQuarterlyPlan>;
type YearlyUpdateReturn = ReturnType<typeof useUpdateManagerYearlyPlan>;

type Props = {
  type: ProgressCardType;
  data: PlanResult;
  canEditPlans: boolean;
  managerId: number | null;
  year: number;
  month: number;
  quarter: number;
  monthlyUpdate: MonthlyUpdateReturn;
  quarterlyUpdate: QuarterlyUpdateReturn;
  yearlyUpdate: YearlyUpdateReturn;
};

export const useManagePlanResults = ({
  type,
  data,
  canEditPlans,
  managerId,
  year,
  month,
  quarter,
  monthlyUpdate,
  quarterlyUpdate,
  yearlyUpdate,
}: Props) => {
  const [isEditPopup, setEditPopup] = useState<boolean>(false);
  const [planValue, setPlanValue] = useState<string>('');
  const queryClient = useQueryClient();

  const openPopup = () => {
    const currentPlan = data.plan;
    setPlanValue(currentPlan != null ? formatNum(currentPlan) : '');
    setEditPopup(true);
  };

  const closePopup = () => setEditPopup(false);

  const onUpdatePlan = async (newPlan: string) => {
    if (!canEditPlans || managerId === null) return;

    const normalizedPlan = newPlan.replace(/\s/g, '').replace(',', '.');

    if (type === 'monthly') {
      await monthlyUpdate.fetch({
        managerId,
        year,
        month,
        plan: normalizedPlan,
      });

      await queryClient.invalidateQueries({
        queryKey: [QUERY_KEY_MANAGER_PERFORMANCE.MONTHLY_RESULT, managerId, year, month],
      });
    } else if (type === 'quarterly') {
      await quarterlyUpdate.fetch({
        managerId,
        year,
        quarter,
        plan: normalizedPlan,
      });

      await queryClient.invalidateQueries({
        queryKey: [QUERY_KEY_MANAGER_PERFORMANCE.QUARTERLY_RESULT, managerId, year, quarter],
      });
    } else {
      await yearlyUpdate.fetch({
        managerId,
        year,
        plan: normalizedPlan,
      });

      await queryClient.invalidateQueries({
        queryKey: [QUERY_KEY_MANAGER_PERFORMANCE.YEARLY_RESULT, managerId, year],
      });
    }

    toast.success('План успешно обновлен');
    setEditPopup(false);
  };

  return { isEditPopup, openPopup, closePopup, planValue, onUpdatePlan };
};
